import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import {
	createTablePagination,
	TableColumn,
	TablePagination,
	TableSearchEvent,
} from '../../../../../shared-components/custom-table/custom-table.types';
import { html } from '../../../../../utils/html-tag-utils';
import { CurrencyPipe } from '@angular/common';
import { CurrencyLocalePipe } from '../../../../../services/currency-locale.pipe';
import { DateLocalePipe } from '../../../../../services/date-locale.pipe';
import { AppContextService } from '../../../../../services/app-context.service';
import { User } from '../../../../../models/User/user';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { ConfirmingPaymentsApiService } from '../../../../../server-api/confirming-payments-api/confirming-payments-api.service';
import {
	AggregateDocumentConfirmingFinancialRequest,
	FinancialMovementResult,
	FinancialMovementSearchCriteria,
} from '../../../../../models/financial-movement/financial-movement';
import { finalize } from 'rxjs';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { CustomTableComponent } from '../../../../../shared-components/custom-table/custom-table.component';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '../../../../../services/translate/translate.service';

@Component({
	selector: 'eif-financial-movements-details-tab',
	templateUrl: './financial-movements-details-tab.component.html',
	styleUrls: ['./financial-movements-details-tab.component.scss'],
	standalone: false,
})
export class FinancialMovementsDetailsTabComponent implements OnInit {
	@ViewChild('customTable') customTable: CustomTableComponent;

	@Input() aggregateDocument: AggregateDocumentConfirmingFinancialRequest;
	@Input() loggedUser: User;
	@Input() documentId: string;

	public financialMovementsResult: FinancialMovementResult[] = [];
	public isPerformingRequest: boolean = false;
	public tablePagination: TablePagination = createTablePagination();

	constructor(
		public appContext: AppContextService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private confirmingPaymentsApiService: ConfirmingPaymentsApiService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.performSearch(1);
	}

	public columns: TableColumn[] = [
		{
			field: 'PaymentRegistrationDate',
			header: 'financial-movements.paymentRegistrationDate',
			align: 'left',
			render: (row: FinancialMovementResult) => {
				const datePipe = new DateLocalePipe(this.appContext);
				return html`<span>${datePipe.transform(new Date(row.PaymentRegistrationDate), 'yyyy/MM/dd') || ''}</span>`;
			},
		},
		{
			field: 'MovementValueDate',
			header: 'financial-movements.movementValueDate',
			align: 'left',
			render: (row: FinancialMovementResult) => {
				const datePipe = new DateLocalePipe(this.appContext);
				return html`<span>${datePipe.transform(new Date(row.MovementValueDate), 'yyyy/MM/dd') || ''}</span>`;
			},
		},
		{
			field: 'AccumulatedAmount',
			header: 'financial-movements.accumulatedAmount',
			align: 'right',
			render: (row: FinancialMovementResult) => {
				const currencyPipe = new CurrencyLocalePipe(new CurrencyPipe(this.appContext.getCurrentLanguage()));
				return html`<span class="is-bold"
					>${currencyPipe.transform(row.DocumentMovementAccumulatedAmount, this.aggregateDocument?.Document?.Currency)}</span
				>`;
			},
		},
		{
			field: 'AmountWithVat',
			header: 'financial-movements.amountWithVat',
			align: 'right',
			render: (row: FinancialMovementResult) => {
				const currencyPipe = new CurrencyLocalePipe(new CurrencyPipe(this.appContext.getCurrentLanguage()));
				return html`<span class="is-bold"
					>${currencyPipe.transform(row.EffectivePaymentValue, this.aggregateDocument?.Document?.Currency)}</span
				>`;
			},
		},
		{
			field: 'PaymentNumber',
			header: 'financial-movements.paymentNumber',
			align: 'left',
			render: (row: FinancialMovementResult) => {
				return html`
					<div>
						<span>${row.FinancierPaymentNumber}</span>
					</div>
				`;
			},
		},
		{
			field: 'PaymentMethod',
			header: 'financial-movements.paymentMethod',
			align: 'left',
			render: (row: FinancialMovementResult) => {
				return html`
					<div>
						<span>${row.FinancierPaymentMethod}</span>
					</div>
				`;
			},
		},
		{
			field: 'BuyerIBAN',
			header: 'financial-movements.buyerIBAN',
			align: 'left',
			render: (row: FinancialMovementResult) => {
				return html`
					<div>
						<span>${row.BuyerIban}</span>
					</div>
				`;
			},
		},
		{
			field: 'FinancierReference',
			header: 'financial-movements.financierReference',
			align: 'left',
			render: (row: FinancialMovementResult) => {
				return html`
					<div>
						<span>${row.BankMovementReference}</span>
					</div>
				`;
			},
		},
		{
			field: 'ProcessingMethod',
			header: 'financial-movements.processingMethod',
			align: 'left',
			render: (row: FinancialMovementResult) => {
				return html`
					<div>
						<span>${row.FinancierProcessingMode}</span>
					</div>
				`;
			},
		},
	];

	public onSearchRequested(event: TableSearchEvent): void {
		this.tablePagination.pageSize = event.pageSize;
		this.performSearch(event.pageIndex + 1);
	}

	public performSearch(pageNumber: number) {
		this.tablePagination.pageIndex = pageNumber - 1;
		this.isPerformingRequest = true;

		const criteria = new FinancialMovementSearchCriteria();
		criteria.OrderBy = 'ActionSeqKey';
		criteria.OrderDescending = true;
		criteria.PageNumber = pageNumber;
		criteria.RowsPerPage = this.tablePagination.pageSize;
		criteria.RestrictionCriteria = new FinancialMovementSearchCriteria();
		criteria.RestrictionCriteria.DocumentId = this.documentId;

		this.confirmingPaymentsApiService
			.searchFinancialMovements(criteria)
			.pipe(
				finalize(() => {
					this.isPerformingRequest = false;
					this.cdr.detectChanges();
				})
			)
			.subscribe({
				next: (operationResult: OperationResult<FinancialMovementResult[]>) => {
					if (operationResult.IsValid) {
						this.financialMovementsResult = operationResult.Data;
						this.tablePagination.totalItems = this.financialMovementsResult.length;
						this.tablePagination = { ...this.tablePagination };

						if (this.financialMovementsResult.length > 0) {
							const firstRow = this.financialMovementsResult[0];
							const totalAmount = firstRow.DocumentMovementAccumulatedAmount + firstRow.EffectivePaymentValue;
							if (Math.abs(totalAmount - this.aggregateDocument?.Document?.TotalAmount) < 0.01) {
								// Using small epsilon for floating point comparison
								firstRow.checkedLifecycle = true;
							}
						}
					} else {
						this.toasterService.showError(this.translateService.translate('INVALID_SEARCH_RESULT', 'list-errorCodes'));
					}

					this.cdr.markForCheck();
					this.customTable?.triggerFinalRerender();
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

					this.customTable?.triggerFinalRerender();
				},
			});
	}
}
